import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import Breadcrumbs from 'components/Breadcrumbs';
import ChemicalCard from 'components/ChemicalCard';
import {
  Sidebar,
  SummarySection,
  StorageSection,
  FlammabilitySection,
  ReactivitySection,
  HandlingSection,
  PPESection,
  FirstAidSection,
  FireSafetySection,
  ToxicitySection,
  TransportationSection,
  EmergencySection
} from 'components/MSDSData';
import { getChemicalProfile } from 'utils/methods';
import { getAllGHSImages } from 'utils/helpers';
import { useLocationStore } from 'context/LocationContext';

const ChemicalProfile = () => {
  const { productName, cid } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { selectedLocationProfile } = useLocationStore();

  const [chemicalDetails, setChemicalDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState('basic-info');
  const [activePpeTab, setActivePpeTab] = useState(null);
  const [summaryData, setSummaryData] = useState({
    hazards: [],
    precautions: []
  });
  const [refreshKey, setRefreshKey] = useState(0);
  
  const sidebarSections = {
    'basic-info': { label: 'Summary', icon: 'fas fa-info-circle' },
    'storage': { label: 'Storage', icon: 'fas fa-warehouse' },
    'handling': { label: 'Handling', icon: 'fas fa-hand-holding' },
    'flammability': { label: 'Flammability', icon: 'fas fa-fire' },
    'toxicity': { label: 'Toxicity', icon: 'fas fa-skull-crossbones' },
    'reactivity': { label: 'Reactivity & stability', icon: 'fas fa-flask' },
    'ppe': { label: 'Personal Protective Equipment', icon: 'fas fa-shield-alt' },
    'first-aid': { label: 'First Aid', icon: 'fas fa-plus-circle' },
    'fire-safety': { label: 'Fire Safety', icon: 'fas fa-fire-extinguisher' },
    'transportation': { label: 'Transportation Guidelines', icon: 'fas fa-truck' },
    'emergency': { label: 'Emergency Guidelines', icon: 'fas fa-exclamation-triangle' }
  };

  const fetchChemicalDetails = useCallback(async (payload) => {
    setLoading(true);
    setError(null);

    const { productName, pdfLinks, cid } = payload;

    try {
      // Clean up the product name
      const cleanProductName = productName.replace(/--+/g, '-').replace(/[^\w\s-]/g, '').trim();

      // Get CAS number from localStorage if available
      const productData = JSON.parse(localStorage.getItem('selectedProduct') || '{}');
      const casNumber = productData.cas_number || '';

      const requestBody = {
        cas: casNumber,
        cid: String(cid || '') // Ensure cid is processed as string
      };

      const { data } = await getChemicalProfile(requestBody);

      if (!data) {
        throw new Error('No chemical data received');
      }

      if (data.status === 'error') {
        throw new Error(data.message);
      }

      // The response structure from backend is { status: "success", data: chemicalProfile }
      const chemicalProfile = data.data || data;
      
      setChemicalDetails(chemicalProfile);
      setSummaryDataFromProfile(chemicalProfile);

      if (data.profile && data.profile.ppe) {
        setActivePpeTab(Object.keys(data.profile.ppe)[0]);
      }

    } catch (error) {
      console.error('Chemical Profile Error:', error);

      if (error.response?.data?.message?.includes('context length')) {
        setError("Too much data to process. Using only the first PDF link.");
      } else {
        setError(error.response?.data?.message || error.message || "Failed to load chemical details");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (productName && cid) {
        const payload = {
          productName: decodeURIComponent(productName),
          pdfLinks: [],
          cid: String(cid) // Ensure cid is processed as string
        };
        await fetchChemicalDetails(payload);
      } else {
        // Fallback to localStorage if route params are not available
        const productData = JSON.parse(localStorage.getItem('selectedProduct') || '{}');

        if (!productData.name || !productData.cid) {
          navigate('/');
          return;
        }

        await fetchChemicalDetails({
          productName: productData.name,
          pdfLinks: productData.links || [],
          cid: String(productData.cid) // Ensure cid is processed as string
        });
      }
    }; 
    
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productName, cid]);

  // Add effect to re-render when location changes
  useEffect(() => {
    // Force re-render by updating refresh key when location changes
    if (selectedLocationProfile) {
      setRefreshKey(prev => prev + 1);
    }
  }, [selectedLocationProfile]);

  const setSummaryDataFromProfile = (data) => {
    const newSummaryData = {
      precautions: [],
      hazards: []
    };

    // Handle precautions - check if it exists and has the right structure
    if (data.precautions && Array.isArray(data.precautions)) {
      newSummaryData.precautions = data.precautions.map(item => {
        // If item has statement property, use it; otherwise use the item directly
        return item;
      });
    }

    // Handle hazards
    if (data.hazards) {
      const hazards = getAllGHSImages(data.hazards);

      // data.hazards.forEach((hazard) => {
      //   if ((/not|no/i.test(hazard.code))) {
      //     hazards.push({ ...hazard, image: null });
      //   }
      // });

      newSummaryData.hazards = hazards;
    }

    setSummaryData(newSummaryData);
  };
  const currentTemperature = useMemo(() => {
    const temp = parseFloat(selectedLocationProfile?.temperature || 0);
    return temp;
  }, [selectedLocationProfile]);

  const isAboveTemperaturePoint = useMemo(() => {
    return (tempPoint) => {
      if (!tempPoint) return false;

      if (typeof tempPoint !== 'string') return currentTemperature > tempPoint;

      // Try to extract Celsius value first
      const celsiusMatch = tempPoint.match(/([-\d.]+)\s*(?:°C|\\u00b0C)/);
      if (celsiusMatch) {
        const temp = parseFloat(celsiusMatch[1]);
        return !isNaN(temp) && currentTemperature > temp;
      }

      // Fallback to Fahrenheit if Celsius not found
      const fahrenheitMatch = tempPoint.match(/([-\d.]+)\s*(?:°F|\\u00b0F)/);
      if (fahrenheitMatch) {
        const tempF = parseFloat(fahrenheitMatch[1]);
        // Convert Fahrenheit to Celsius for comparison
        const tempC = (tempF - 32) * (5 / 9);
        return !isNaN(tempC) && currentTemperature > tempC;
      }
      return false;
    };
  }, [currentTemperature]);

  // Helper function to extract temperature value in Celsius
  const getTemperatureInCelsius = (tempPoint) => {
    if (!tempPoint) return null;
    
    if (typeof tempPoint !== 'string') return tempPoint;

    // Try to extract Celsius value first
    const celsiusMatch = tempPoint.match(/([-\d.]+)\s*(?:°C|\\u00b0C)/);
    if (celsiusMatch) {
      const temp = parseFloat(celsiusMatch[1]);
      return !isNaN(temp) ? temp : null;
    }

    // Fallback to Fahrenheit if Celsius not found
    const fahrenheitMatch = tempPoint.match(/([-\d.]+)\s*(?:°F|\\u00b0F)/);
    if (fahrenheitMatch) {
      const tempF = parseFloat(fahrenheitMatch[1]);
      // Convert Fahrenheit to Celsius
      const tempC = (tempF - 32) * (5 / 9);
      return !isNaN(tempC) ? tempC : null;
    }
    return null;
  };

  // Check if substance is solid (melting point > 100°C)
  const isSolid = useMemo(() => {
    const meltingPointTemp = getTemperatureInCelsius(chemicalDetails?.storage?.melting_point);
    return meltingPointTemp !== null && meltingPointTemp > 100;
  }, [chemicalDetails]);

  const isLiquid = useMemo(() => {
    const meltingPointTemp = getTemperatureInCelsius(chemicalDetails?.storage?.melting_point);
    return meltingPointTemp !== null && meltingPointTemp < 0;
  }, [chemicalDetails]);

  // Check if substance is gas (boiling point < 0°C)
  const isGas = useMemo(() => {
    const boilingPointTemp = getTemperatureInCelsius(chemicalDetails?.storage?.boiling_point);
    return boilingPointTemp !== null && boilingPointTemp < 0;
  }, [chemicalDetails]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white via-blue-50 to-blue-100">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center h-96">
            <div className="animate-spin rounded-full h-32 w-32 border-8 border-t-8 border-gray-200 border-t-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-gray-50" key={refreshKey}>
      <div className="w-full px-4 md:px-8 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-8 py-8 bg-gradient-to-br from-gray-50 to-blue-50">

        <div className="col-span-5">
          <Breadcrumbs />
        </div>

        {/* Sidebar */}
        <div className="col-span-1">
          <Sidebar 
            sidebarSections={sidebarSections}
            activeSection={activeSection}
            setActiveSection={setActiveSection}
          />
        </div>

        {/* Main content */}
        <div className="col-span-5 md:col-span-2 lg:col-span-4 space-y-8">

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
              {error}
            </div>
          )}

          {/* Chemical Card */}
          {chemicalDetails && (
            <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-lg hover:shadow-xl transition-all duration-300">
              <ChemicalCard chemicalDetails={chemicalDetails} />
            </div>
          )}

          {/* Content Sections */}
          {chemicalDetails && (
            <div className="space-y-8">

              {/* Basic Information / Summary Section */}
              {activeSection === 'basic-info' && (
                <SummarySection 
                  summaryData={summaryData}
                />
              )}

              {/* Storage Information */}
              {activeSection === 'storage' && (
                <StorageSection 
                  chemicalDetails={chemicalDetails}
                  isGas={isGas}
                  isSolid={isSolid}
                  isLiquid={isLiquid}
                  isAboveTemperaturePoint={isAboveTemperaturePoint}
                />
              )}

              {/* Flammability Information */}
              {activeSection === 'flammability' && (
                <FlammabilitySection 
                  chemicalDetails={chemicalDetails}
                  isGas={isGas}
                  isAboveTemperaturePoint={isAboveTemperaturePoint}
                />
              )}

              {/* Reactivity Information */}
              {activeSection === 'reactivity' && (
                <ReactivitySection 
                  chemicalDetails={chemicalDetails}
                />
              )}

              {/* Handling */}
              {activeSection === 'handling' && (
                <HandlingSection 
                  chemicalDetails={chemicalDetails}
                />
              )}

              {/* PPE */}
              {activeSection === 'ppe' && (
                <PPESection 
                  chemicalDetails={chemicalDetails}
                />
              )}

              {/* Fire Safety */}
              {activeSection === 'fire-safety' && (
                <FireSafetySection 
                  chemicalDetails={chemicalDetails}
                />
              )}

              {/* First Aid */}
              {activeSection === 'first-aid' && (
                <FirstAidSection 
                  chemicalDetails={chemicalDetails}
                />
              )}

              {/* Toxicity */}
              {activeSection === 'toxicity' && (
                <ToxicitySection 
                  chemicalDetails={chemicalDetails}
                />
              )}

              {/* Transportation Guidelines */}
              {activeSection === 'transportation' && (
                <TransportationSection chemicalDetails={chemicalDetails} />
              )}

              {/* Emergency Guidelines */}
              {activeSection === 'emergency' && (
                <EmergencySection 
                  chemicalDetails={chemicalDetails}
                />
              )}

            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChemicalProfile;
